import type { SiteConfig } from "@/types/sites";
import type { ThemeConfig } from "@/types/theme";
import { twj } from "tw-to-css";
import type React from "react";
// Lazy palette loader and names list (does not synchronously import palettes)
import {
  loadPalette as loadPaletteLazy,
  getAvailablePaletteNames as listAvailablePalettes,
} from "./palettes";

// --- Debug helpers ---------------------------------------------------------
type DebugCounters = {
  getPaletteForTheme: number;
  getPaletteForThemeAsync: number;
  getPaletteClassCalls: number;
  getPaletteClassCacheHits: number;
  getPaletteClassCacheMisses: number;
  twjCacheHits: number;
  twjCacheMisses: number;
  convertThemeClassesToStylesCalls: number;
  getPaletteStyleCalls: number;
  getFontSizeStyleCalls: number;
  getTextAlignStyleCalls: number;
};

const debugCounters: DebugCounters = {
  getPaletteForTheme: 0,
  getPaletteForThemeAsync: 0,
  getPaletteClassCalls: 0,
  getPaletteClassCacheHits: 0,
  getPaletteClassCacheMisses: 0,
  twjCacheHits: 0,
  twjCacheMisses: 0,
  convertThemeClassesToStylesCalls: 0,
  getPaletteStyleCalls: 0,
  getFontSizeStyleCalls: 0,
  getTextAlignStyleCalls: 0,
};

const paletteDebugState = {
  enabled:
    // Enable by default in dev builds
    import.meta.env.VITE_DEV || false,
};

export function enablePaletteDebug(value = true) {
  paletteDebugState.enabled = value;
}

export function getPaletteDebugStats(): DebugCounters {
  return { ...debugCounters };
}

export function resetPaletteDebugStats(): void {
  (Object.keys(debugCounters) as Array<keyof DebugCounters>).forEach((k) => {
    debugCounters[k] = 0 as never;
  });
}

// Track per-property usage for caching insights
const palettePropertyUsage = new Map<string, number>();

function bumpUsage(
  property?: keyof NonNullable<ThemeConfig["classes"]>,
  fallbackClass?: string
) {
  const key = `${String(property || "")}|${fallbackClass || ""}`;
  const n = palettePropertyUsage.get(key) || 0;
  palettePropertyUsage.set(key, n + 1);
}

export function getMostUsedPaletteClasses(
  topN = 30
): Array<{ key: string; count: number }> {
  return Array.from(palettePropertyUsage.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}

/**
 * Enhanced palette lookup utility that supports both direct palette name
 * and fallback to theme-based color matching
 */

export interface PaletteLookupResult {
  palette: ThemeConfig | null;
  isDirectLookup: boolean;
  fallbackToTheme: boolean; // retained for compatibility, always false in clean mode
}

// tw-to-css results cache to avoid repeated heavy conversions per render
const twjResultCache = new Map<string, React.CSSProperties>();
const MAX_TWJ_CACHE_ENTRIES = 2000;

function getTwjCachedStyles(tailwindClass: string): React.CSSProperties {
  const key = tailwindClass.trim();
  const cached = twjResultCache.get(key);
  if (cached) {
    debugCounters.twjCacheHits++;
    return cached;
  }

  const styles = twj(key) as unknown as React.CSSProperties;
  twjResultCache.set(key, styles);
  debugCounters.twjCacheMisses++;

  // Simple cap to bound memory usage
  if (twjResultCache.size > MAX_TWJ_CACHE_ENTRIES) {
    twjResultCache.clear();
  }
  return styles;
}

/**
 * Get the active palette for a theme configuration
 *
 * Priority order:
 * 1. Direct lookup by paletteName if specified
 * 2. Fallback to color matching if no paletteName
 * 3. Return null if no match (use theme overrides)
 */
// Synchronous palette resolution: never triggers loading, only uses theme.classes
export const getPaletteForTheme = (
  theme?: SiteConfig["theme"]
): PaletteLookupResult => {
  debugCounters.getPaletteForTheme++;
  // If classes already exist on theme (hooks pre-merged palette), synthesize a palette object
  if (theme?.classes) {
    const synthesized: ThemeConfig = {
      paletteName: theme.paletteName,
      darkMode: theme.darkMode || false,
      classes: theme.classes,
    };
    return {
      palette: synthesized,
      isDirectLookup: Boolean(theme.paletteName),
      fallbackToTheme: false,
    };
  }

  // No classes available synchronously
  return {
    palette: null,
    isDirectLookup: Boolean(theme?.paletteName),
    fallbackToTheme: false,
  };
};

// Async palette resolution: loads palette classes if not already present on theme
const palettePromiseCache = new Map<string, Promise<ThemeConfig | null>>();
export const getPaletteForThemeAsync = async (
  theme?: SiteConfig["theme"]
): Promise<PaletteLookupResult> => {
  debugCounters.getPaletteForThemeAsync++;
  if (theme?.classes) {
    // Already have classes merged by the hooks
    return getPaletteForTheme(theme);
  }

  const name = theme?.paletteName;
  if (!name) {
    return { palette: null, isDirectLookup: false, fallbackToTheme: false };
  }

  // De-duplicate concurrent loads
  let promise = palettePromiseCache.get(name);
  if (!promise) {
    promise = loadPaletteLazy(name).catch(() => {
      // ignore palette load errors
      return null;
    });
    palettePromiseCache.set(name, promise);
  }

  const palette = await promise;
  return {
    palette,
    isDirectLookup: Boolean(palette),
    fallbackToTheme: false,
  };
};

/**
 * Convert a mapping of CSS style properties to theme class keys into inline styles.
 * Example:
 *   convertThemeClassesToStyles(theme, { backgroundColor: 'backgroundPrimary', padding: 'spacingSection' })
 * This will lookup the Tailwind classes for each provided key via getPaletteClass
 * and convert them into a React.CSSProperties object using tw-to-css.
 */
export const convertThemeClassesToStyles = (
  _theme?: SiteConfig["theme"],
  mapping?: Record<string, keyof NonNullable<ThemeConfig["classes"]> | string>,
  fallbacks?: Record<string, string>
): React.CSSProperties => {
  debugCounters.convertThemeClassesToStylesCalls++;
  if (!mapping) return {};

  const combinedStyles: React.CSSProperties = {};

  try {
    Object.entries(mapping).forEach(([styleProp, classKeyOrClass]) => {
      // Resolve the Tailwind class string: if the key exists in classes, use it via getPaletteClass
      // otherwise assume it's already a Tailwind class string
      const fallback = fallbacks?.[styleProp];
      const tailwindClass =
        typeof classKeyOrClass === "string"
          ? // Try to treat as a classes key first; if not found, use as a raw class
            getPaletteClass(
              _theme,
              classKeyOrClass as keyof NonNullable<ThemeConfig["classes"]>,
              fallback
            ) || classKeyOrClass
          : getPaletteClass(_theme, classKeyOrClass, fallback);

      if (!tailwindClass) return;

      const css = getTwjCachedStyles(tailwindClass);

      // Only pick the relevant style property if it exists in the computed CSS
      // Otherwise, merge all computed styles as a fallback
      if (styleProp in css) {
        // @ts-expect-error: dynamic assignment based on computed styles
        combinedStyles[styleProp] = css[styleProp as keyof React.CSSProperties];
      } else {
        Object.assign(combinedStyles, css);
      }
    });
  } catch {
    // ignore conversion errors
  }

  return combinedStyles;
};

/**
 * Get Tailwind classes for a specific property from the active palette
 * Returns theme-based fallback if no palette is active
 */
// Memoization cache for palette class lookups
const paletteClassCache = new Map<string, string>();

export const getPaletteClass = (
  theme?: SiteConfig["theme"],
  property?: keyof NonNullable<ThemeConfig["classes"]>,
  fallbackClass?: string
): string => {
  debugCounters.getPaletteClassCalls++;
  bumpUsage(property, fallbackClass);
  // Create efficient cache key (avoid expensive JSON.stringify)
  const themeSig = `${theme?.paletteName || "no-palette"}|${theme?.darkMode ? 1 : 0}|${Object.keys(theme?.classes || {}).length}`;
  const cacheKey = `${themeSig}-${property}-${fallbackClass}`;

  // Check cache first
  if (paletteClassCache.has(cacheKey)) {
    debugCounters.getPaletteClassCacheHits++;
    return paletteClassCache.get(cacheKey)!;
  }

  // Use theme.classes synchronously; hooks ensure palette classes are merged
  const sourceClasses = theme?.classes;
  const result =
    sourceClasses && property
      ? sourceClasses[property] || fallbackClass || ""
      : fallbackClass || "";

  // Store in cache
  paletteClassCache.set(cacheKey, result);
  debugCounters.getPaletteClassCacheMisses++;

  // Clear cache if it gets too large (memory management)
  if (paletteClassCache.size > 1000) {
    paletteClassCache.clear();
  }

  return result;
};

/**
 * Get available palette names for configuration
 */
export const getAvailablePaletteNames = (): string[] => {
  return listAvailablePalettes();
};

/**
 * Validate if a palette name exists
 */
export const isPaletteNameValid = (paletteName: string): boolean => {
  return listAvailablePalettes().includes(paletteName);
};

/**
 * Get inline CSS styles for a specific property from the active palette
 * Converts Tailwind classes to CSS-in-JS style object using tw-to-css
 *
 * @param theme - The site theme configuration
 * @param property - The palette property to get styles for
 * @param fallbackClass - Fallback Tailwind classes if palette property not found
 * @returns CSS-in-JS style object
 */
export const getPaletteStyle = (
  theme?: SiteConfig["theme"],
  property?: keyof NonNullable<ThemeConfig["classes"]>,
  fallbackClass?: string
): React.CSSProperties => {
  debugCounters.getPaletteStyleCalls++;
  const paletteClass = getPaletteClass(theme, property, fallbackClass);

  if (!paletteClass) {
    return {};
  }

  try {
    // Convert Tailwind classes to CSS-in-JS object
    return getTwjCachedStyles(paletteClass);
  } catch {
    return {};
  }
};

/**
 * Get font size CSS style from Tailwind classes
 * Extracts only font-size related properties from the classes
 *
 * @param tailwindClass - Tailwind classes containing font size (e.g., "text-xl", "text-2xl")
 * @returns CSS-in-JS style object with fontSize property
 */
export const getFontSizeStyle = (
  tailwindClass?: string
): React.CSSProperties => {
  debugCounters.getFontSizeStyleCalls++;
  if (!tailwindClass) {
    return {};
  }

  try {
    // Convert Tailwind classes to CSS-in-JS object
    const allStyles = getTwjCachedStyles(tailwindClass);

    // Extract only font-size related properties
    const fontSizeStyles: React.CSSProperties = {};
    if (allStyles.fontSize) {
      fontSizeStyles.fontSize = allStyles.fontSize;
    }
    if (allStyles.lineHeight) {
      fontSizeStyles.lineHeight = allStyles.lineHeight;
    }

    return fontSizeStyles;
  } catch {
    return {};
  }
};

/**
 * Get text alignment CSS style from Tailwind classes
 * Extracts only text-align related properties from the classes
 *
 * @param tailwindClass - Tailwind classes containing text alignment (e.g., "text-left", "text-center", "text-right")
 * @returns CSS-in-JS style object with textAlign property
 */
export const getTextAlignStyle = (
  tailwindClass?: string
): React.CSSProperties => {
  debugCounters.getTextAlignStyleCalls++;
  if (!tailwindClass) {
    return {};
  }

  try {
    // Convert Tailwind classes to CSS-in-JS object
    const allStyles = getTwjCachedStyles(tailwindClass);

    // Extract only text-align related properties
    const textAlignStyles: React.CSSProperties = {};
    if (allStyles.textAlign) {
      textAlignStyles.textAlign = allStyles.textAlign;
    }

    return textAlignStyles;
  } catch {
    return {};
  }
};

/**
 * Convert CSS font size value to pixels
 * Handles rem, em, px, pt units and converts them to pixel values
 *
 * @param cssValue - CSS font size value (e.g., "3rem", "24px", "1.5em", "12pt")
 * @param defaultValue - Default pixel value to return if conversion fails (default: 16)
 * @returns Font size in pixels as a number
 */
export const convertFontSizeToPixels = (
  cssValue?: string | number,
  defaultValue: number = 16
): number => {
  if (!cssValue) {
    return defaultValue;
  }

  // If it's already a number, return it
  if (typeof cssValue === "number") {
    return cssValue;
  }

  const fontSizeValue = cssValue.toString();

  try {
    // Convert different units to pixels
    if (fontSizeValue.includes("rem")) {
      // Convert rem to pixels (assuming 1rem = 16px)
      const remValue = parseFloat(fontSizeValue);
      return remValue * 16;
    } else if (fontSizeValue.includes("em")) {
      // Convert em to pixels (assuming 1em = 16px at root)
      const emValue = parseFloat(fontSizeValue);
      return emValue * 16;
    } else if (fontSizeValue.includes("px")) {
      // Already in pixels
      return parseFloat(fontSizeValue);
    } else if (fontSizeValue.includes("pt")) {
      // Convert points to pixels (1pt = 1.333px)
      const ptValue = parseFloat(fontSizeValue);
      return ptValue * 1.333;
    } else {
      // Try to parse as a number
      const parsed = parseFloat(fontSizeValue);
      if (!isNaN(parsed)) {
        return parsed;
      }
    }
  } catch {
    /* ignore font size conversion errors */
  }

  return defaultValue;
};

// Optional helper to dump a one-shot summary to the console
export function logPaletteDebugSummary(): void {
  // logging disabled
}

/**
 * Batch resolve palette classes to reduce repeated lookups in components.
 */
export function resolvePaletteClasses(
  theme: SiteConfig["theme"] | undefined,
  keys: Array<keyof NonNullable<ThemeConfig["classes"]>>,
  fallbacks?: Partial<Record<keyof NonNullable<ThemeConfig["classes"]>, string>>
): Partial<Record<keyof NonNullable<ThemeConfig["classes"]>, string>> {
  const out: Partial<
    Record<keyof NonNullable<ThemeConfig["classes"]>, string>
  > = {};
  for (const k of keys) {
    out[k] = getPaletteClass(theme, k, fallbacks?.[k]);
  }
  return out;
}

/**
 * Get a standard pack of commonly used classes for section components.
 * This helps avoid repeating resolvePaletteClasses calls across components.
 */
export function getCommonSectionClasses(
  theme?: SiteConfig["theme"],
  overrides?: Partial<Record<keyof NonNullable<ThemeConfig["classes"]>, string>>
): Partial<Record<keyof NonNullable<ThemeConfig["classes"]>, string>> {
  return resolvePaletteClasses(
    theme,
    [
      "textPrimary",
      "textSecondary",
      "textMuted",
      "textOnAccent",
      "textInverse",
      "textAccent",
      "backgroundPrimary",
      "backgroundSecondary",
      "backgroundSurface",
      "backgroundCard",
      "backgroundAccent",
      "borderDefault",
      "borderPrimary",
      "borderSecondary",
      "shadowDefault",
      "shadowHover",
      "primary",
      "secondary",
      "accent",
      "gradientPrimary",
    ],
    {
      textPrimary: "text-gray-900 dark:text-white",
      textSecondary: "text-gray-600 dark:text-gray-300",
      textMuted: "text-gray-500 dark:text-gray-400",
      textOnAccent: "text-white",
      textInverse: "text-white",
      textAccent: "text-blue-600 dark:text-blue-400",
      backgroundPrimary: "bg-white dark:bg-gray-900",
      backgroundSecondary: "bg-gray-100 dark:bg-gray-800",
      backgroundSurface: "bg-white dark:bg-gray-900",
      backgroundCard: "bg-gray-100 dark:bg-gray-800",
      backgroundAccent: "bg-blue-600 dark:bg-blue-500",
      borderDefault: "border-gray-300 dark:border-gray-600",
      borderPrimary: "border-blue-600 dark:border-blue-500",
      borderSecondary: "border-gray-300 dark:border-gray-600",
      shadowDefault: "shadow-lg",
      shadowHover: "hover:shadow-xl dark:hover:shadow-slate-600/60",
      primary: "text-blue-600 dark:text-blue-400",
      secondary: "text-blue-500",
      accent: "text-blue-400",
      gradientPrimary:
        "bg-gradient-to-r from-transparent via-gray-300/50 to-transparent dark:via-gray-600/50",
      ...(overrides || {}),
    }
  );
}

// Type declaration for palette debug tools
interface PaletteDebugTools {
  enable: typeof enablePaletteDebug;
  reset: typeof resetPaletteDebugStats;
  stats: typeof getPaletteDebugStats;
  summary: typeof logPaletteDebugSummary;
  top: typeof getMostUsedPaletteClasses;
}

declare global {
  interface Window {
    paletteDebug?: PaletteDebugTools;
  }
}

// In dev, expose handy debug controls on window for console usage (no logs by default)
try {
  if (typeof window !== "undefined" && import.meta.env.VITE_DEV) {
    window.paletteDebug = {
      enable: enablePaletteDebug,
      reset: resetPaletteDebugStats,
      stats: getPaletteDebugStats,
      summary: logPaletteDebugSummary,
      top: getMostUsedPaletteClasses,
    };
  }
} catch {
  /* ignore binding errors */
}

// ═══════════════════════════════════════════════════════════════════════════
// TRADE ACCENT CSS VARIABLE BRIDGE
// ═══════════════════════════════════════════════════════════════════════════
//
// Maps Tailwind color names to RGB values so we can set --trade-accent-rgb
// from the palette's backgroundAccentColor class at runtime.
//
// This bridges the palette class system (Tailwind classes) with CSS variables
// needed for inline styles (box-shadow, gradients, etc.) in Trade design.

/** Tailwind color name+shade → RGB triplet (space-separated) */
const TAILWIND_COLOR_RGB: Record<string, string> = {
  "amber-400": "251 191 36",
  "amber-500": "245 158 11",
  "blue-400": "96 165 250",
  "blue-500": "59 130 246",
  "cyan-400": "34 211 238",
  "cyan-500": "6 182 212",
  "emerald-400": "52 211 153",
  "emerald-500": "16 185 129",
  "fuchsia-400": "232 121 249",
  "fuchsia-500": "217 70 239",
  "indigo-400": "129 140 248",
  "indigo-500": "99 102 241",
  "lime-400": "163 230 53",
  "lime-500": "132 204 22",
  "orange-400": "251 146 60",
  "orange-500": "249 115 22",
  "pink-400": "244 114 182",
  "pink-500": "236 72 153",
  "red-400": "248 113 113",
  "red-500": "239 68 68",
  "rose-400": "251 113 133",
  "rose-500": "244 63 94",
  "sky-400": "56 189 248",
  "sky-500": "14 165 233",
  "teal-300": "94 234 212",
  "teal-400": "45 212 191",
  "teal-500": "20 184 166",
  "violet-400": "167 139 250",
  "violet-500": "139 92 246",
};

/**
 * Extract Tailwind color-shade from a bg class string.
 * e.g. "bg-orange-500 dark:bg-orange-400" → { light: "orange-500", dark: "orange-400" }
 */
function parseBgColorClass(bgClass: string): { light: string; dark: string } | null {
  const lightMatch = bgClass.match(/(?:^|\s)bg-([a-z]+-\d+)/);
  const darkMatch = bgClass.match(/dark:bg-([a-z]+-\d+)/);
  if (!lightMatch) return null;
  return {
    light: lightMatch[1],
    dark: darkMatch ? darkMatch[1] : lightMatch[1],
  };
}

/**
 * Apply Trade accent CSS variables from the palette's accent color.
 *
 * Reads `backgroundAccentColor` from the theme, extracts the Tailwind color,
 * converts to RGB, and sets `--trade-accent-rgb` on the document root.
 * The `.dark` selector in _variables.scss uses `--trade-accent-dark-rgb`,
 * which is also set here.
 *
 * @param theme - The site theme configuration (with palette classes merged)
 */
export function applyTradeAccentCssVars(theme?: SiteConfig["theme"]): void {
  if (typeof document === "undefined") return;

  const accentClass = theme?.classes?.backgroundAccentColor;
  if (!accentClass) return;

  const parsed = parseBgColorClass(accentClass);
  if (!parsed) return;

  const lightRgb = TAILWIND_COLOR_RGB[parsed.light];
  const darkRgb = TAILWIND_COLOR_RGB[parsed.dark];

  if (!lightRgb) return;

  const root = document.documentElement;
  root.style.setProperty("--trade-accent-rgb", lightRgb);
  root.style.setProperty("--trade-accent-dark-rgb", darkRgb || lightRgb);
  root.style.setProperty("--trade-accent", `rgb(${lightRgb})`);
}
